/**
 * The node a miner's blocks were reported from.
 *
 * **Two layers of uncertainty, and the panel has to wear both.** The chain says
 * who was paid and nothing about what won the block; everything here is a node's
 * report about itself, reached through the same first-reporter voting that gave
 * the miner its display name. So the confidence and the vote count sit at the
 * top of the panel rather than in a footnote, and the heading says "reported
 * by", not "mined on".
 *
 * The hardware is the sharpest case. A CPU model beside a hashrate reads as the
 * thing doing the hashing, and on this network it is usually not: the miners
 * measured all report `miner_cpu_hash_rate` of zero, so the work is on GPUs that
 * telemetry does not mention at all. The note says so.
 *
 * Location is a country and never finer — see `blackbeard_core::geo`.
 */

import type { NodeInfo } from '../api/generated/NodeInfo'
import type { AttributionSource } from '../api/generated/AttributionSource'
import { Link } from 'react-router-dom'

import { ago, bandwidth, bytes, since, utc } from '../lib/format'
import { href } from '../lib/routes'
import { Field } from './Field'

/** A two-letter country code as its flag, by offsetting into the regional
 *  indicator block. Returns null for anything that is not two letters, so a
 *  malformed code renders as nothing rather than as tofu. */
function flag(code: string | null): string | null {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return null
  const base = 0x1f1e6 - 'A'.charCodeAt(0)
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => base + c.charCodeAt(0)))
}

export function NodePanel({
  node,
  confidence,
  votes,
  attribution,
  chain,
}: {
  node: NodeInfo
  confidence: number | null
  votes: number | null
  attribution: AttributionSource | null
  /** Set where the node's own page is somewhere else to go. Omitted on that
   *  page, where the heading would link to itself. */
  chain?: string | undefined
}) {
  const os = [node.target_os, node.target_arch, node.target_env].filter(Boolean).join(' · ')
  const country = node.country_code
    ? `${flag(node.country_code) ?? ''} ${node.country_code}`.trim()
    : null

  // A percentage on its own cannot tell one vote of one from nine of nine, and
  // those are not the same claim — so both travel together, as they do on the
  // standings.
  const strength =
    confidence !== null && votes !== null
      ? `${Math.round(confidence * 100)}% of ${votes} vote${votes === 1 ? '' : 's'}`
      : null

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <div>
          <div className="eyebrow">Reported by</div>
          <h2 className="panel-title" style={{ marginTop: 4, textTransform: 'none' }}>
            {chain ? (
              // Only when this panel sits under something else. On the node's
              // own page the heading would link to where the reader already is.
              <Link to={href({ chain, node: node.peer_id })}>{node.name}</Link>
            ) : (
              node.name
            )}
          </h2>
        </div>
        {strength && (
          <span
            className="eyebrow"
            title="How much of the telemetry voting agrees that this miner's blocks come from this node."
          >
            attributed on {strength}
          </span>
        )}
      </div>

      <div className="node-grid">
        {node.version && <Field label="Version" value={node.version} />}
        {node.implementation && <Field label="Client" value={node.implementation} />}
        {os && <Field label="Target" value={os} />}
        {country && (
          <Field label="Country" value={country} title={node.country_name ?? undefined} />
        )}
        {node.cpu && <Field label="CPU" value={node.cpu} title={node.cpu} />}
        {node.core_count !== null && <Field label="Cores" value={String(node.core_count)} />}
        {node.memory !== null && <Field label="Memory" value={bytes(node.memory)} />}
        {node.linux_distro && <Field label="Distribution" value={node.linux_distro} />}
        {node.linux_kernel && <Field label="Kernel" value={node.linux_kernel} />}
        {node.is_virtual_machine !== null && (
          <Field label="Machine" value={node.is_virtual_machine ? 'virtual' : 'bare metal'} />
        )}
        {node.startup_time && (
          <Field
            label="Node uptime"
            value={since(node.startup_time)}
            title={`Started ${utc(node.startup_time)}`}
          />
        )}
      </div>

      {node.activity && (
        <>
          <div className="eyebrow" style={{ padding: '14px 16px 0' }}>
            Right now
          </div>
          <div className="node-grid" style={{ borderTop: 0, marginTop: 8 }}>
            {node.activity.peer_count !== null && (
              <Field label="Peers" value={String(node.activity.peer_count)} />
            )}
            {node.activity.transactions_in_queue !== null && (
              <Field label="In its pool" value={String(node.activity.transactions_in_queue)} />
            )}
            {node.activity.upload_bytes_per_second !== null && (
              <Field label="Upload" value={bandwidth(node.activity.upload_bytes_per_second)} />
            )}
            {node.activity.download_bytes_per_second !== null && (
              <Field label="Download" value={bandwidth(node.activity.download_bytes_per_second)} />
            )}
            {node.activity.state_cache_bytes !== null && (
              <Field label="State cache" value={bytes(node.activity.state_cache_bytes)} />
            )}
          </div>
        </>
      )}

      <p className="panel-note">
        Everything here is what the node says about itself on the telemetry feed, and the link
        between it and this miner is <em>inferred</em> from which node reported its blocks first —
        never a claim the operator made, and never an identity check.{' '}
        {attribution === 'telemetry'
          ? 'One operator may run several nodes under one reward preimage, and one node may mine for several, so this is the node the attribution settled on rather than the miner’s only machine.'
          : ''}{' '}
        The CPU is the node’s, not necessarily what is hashing: on this network most work is on
        GPUs, which telemetry does not report. Location is resolved to a country and no finer,
        because the feed’s coordinates come from IP geolocation and are unreliable at city
        resolution.
        {node.activity
          ? ' The figures under “right now” are read straight off the telemetry socket and are never stored — an absent one means the feed has not reported it, not that it is zero.'
          : ''}
        {node.seen_at && <> Last heard {ago(node.seen_at)}.</>}
      </p>
    </section>
  )
}
